import { auth, db } from "../config/firebase";
import { Model, Order } from "@beyondrealityapp/core/shared/constants";
import { Comment } from "@beyondrealityapp/core/comment/classes";
import { CommentDocument } from "@beyondrealityapp/core/comment/types";
import { CommentField } from "@beyondrealityapp/core/comment/constants";
import { SharedUserSummary } from "@beyondrealityapp/core/shared/types";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  getCountFromServer,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  setDoc,
  where,
} from "firebase/firestore";

type FetchCommentsArgs = {
  parentModel: Model;
  parentId: string;
};

type AddCommentArgs = {
  parentModel: Model;
  parentId: string;
  content: string;
  mentions: string[];
  sharedWithUIDs: string[];
  authorName: string;
  authorProfilePicture: string;
  sharedWithUsers: SharedUserSummary[];
};

export const commentsApi = createApi({
  reducerPath: "commentsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Comment", "CommentCount"],
  endpoints: (builder) => ({
    fetchComments: builder.query<Comment[], FetchCommentsArgs>({
      queryFn: async ({ parentModel, parentId }) => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { data: [] };
        try {
          const comments = await _fetchComments(uid, parentModel, parentId);
          return { data: comments };
        } catch (error) {
          return { error: error as Error };
        }
      },
      async onCacheEntryAdded(
        { parentModel, parentId },
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        await cacheDataLoaded;
        const q = _buildCommentsQuery(uid, parentModel, parentId, true);
        const unsubscribe = onSnapshot(q, (snapshot) => {
          updateCachedData(() => snapshot.docs.map(_docToComment));
        });
        await cacheEntryRemoved;
        unsubscribe();
      },
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "Comment" as const,
                id,
              })),
              { type: "Comment", id: `${arg.parentModel}-${arg.parentId}` },
            ]
          : [{ type: "Comment", id: `${arg.parentModel}-${arg.parentId}` }],
    }),
    fetchCommentCount: builder.query<number, FetchCommentsArgs>({
      queryFn: async ({ parentModel, parentId }) => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { data: 0 };
        try {
          const count = await _fetchCommentCount(uid, parentModel, parentId);
          return { data: count };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: "CommentCount", id: `${arg.parentModel}-${arg.parentId}` },
      ],
    }),
    addComment: builder.mutation<Comment, AddCommentArgs>({
      queryFn: async (args) => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { error: new Error("Not authenticated") };
        try {
          const comment = await _addComment(uid, args);
          return { data: comment };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "Comment", id: `${arg.parentModel}-${arg.parentId}` },
        { type: "CommentCount", id: `${arg.parentModel}-${arg.parentId}` },
      ],
    }),
    deleteComment: builder.mutation<
      string,
      { commentId: string; parentModel: Model; parentId: string }
    >({
      queryFn: async ({ commentId }) => {
        try {
          const id = await _deleteComment(commentId);
          return { data: id };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "Comment", id: arg.commentId },
        { type: "Comment", id: `${arg.parentModel}-${arg.parentId}` },
        { type: "CommentCount", id: `${arg.parentModel}-${arg.parentId}` },
      ],
    }),
  }),
});

export const {
  useFetchCommentsQuery,
  useFetchCommentCountQuery,
  useAddCommentMutation,
  useDeleteCommentMutation,
} = commentsApi;

const _buildCommentsQuery = (
  uid: string,
  parentModel: Model,
  parentId: string,
  ordered: boolean = false
) => {
  const constraints: QueryConstraint[] = [
    where(CommentField.PARENT_MODEL, "==", parentModel),
    where(CommentField.PARENT_ID, "==", parentId),
    where(CommentField.SHARED_WITH_UIDS, "array-contains", uid),
  ];
  if (ordered) constraints.push(orderBy(CommentField.CREATED_AT, Order.ASCENDING));
  return query(collection(db, Model.COMMENTS), ...constraints);
};

const _docToComment = (doc: QueryDocumentSnapshot<DocumentData, DocumentData>): Comment =>
  Comment.fromFirestore({ ...doc.data(), id: doc.id } as CommentDocument);

const _fetchComments = async (
  uid: string,
  parentModel: Model,
  parentId: string
): Promise<Comment[]> => {
  const q = _buildCommentsQuery(uid, parentModel, parentId, true);
  const snapshot = await getDocs(q);
  return snapshot.docs.map(_docToComment);
};

const _fetchCommentCount = async (
  uid: string,
  parentModel: Model,
  parentId: string
): Promise<number> => {
  const q = _buildCommentsQuery(uid, parentModel, parentId);
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

const _addComment = async (uid: string, args: AddCommentArgs): Promise<Comment> => {
  const sharedWithUIDs = [...args.sharedWithUIDs];
  if (!sharedWithUIDs.includes(uid)) sharedWithUIDs.push(uid);

  // Ensure the parent record owner can always see comments on their record
  const parentRef = doc(db, args.parentModel, args.parentId);
  const parentSnap = await getDoc(parentRef);
  const ownerUid = parentSnap.data()?.uid;
  if (ownerUid && !sharedWithUIDs.includes(ownerUid)) sharedWithUIDs.push(ownerUid);
  const comment = new Comment({
    parentModel: args.parentModel,
    parentId: args.parentId,
    content: args.content,
    mentions: args.mentions,
    isAiBuddy: false,
    sharedWithUIDs,
    authorName: args.authorName,
    authorProfilePicture: args.authorProfilePicture,
    sharedWithUsers: args.sharedWithUsers,
  });
  comment.init(uid);
  const commentRef = doc(db, Model.COMMENTS, comment.id);
  await setDoc(commentRef, comment.toFirestore());
  return comment;
};

const _deleteComment = async (commentId: string): Promise<string> => {
  const commentRef = doc(db, Model.COMMENTS, commentId);
  await deleteDoc(commentRef);
  return commentId;
};
